import { useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface EconomyResult {
  daily_snapshots_count: number;
  faucet_sink_ratios: Record<string, number>;
  inflation_alerts: string[];
  warnings: string[];
}

interface ProgressionResult {
  profile: string;
  days_to_max: number;
  bottlenecks: string[];
  levels: number;
}

interface LootResult {
  total_rolls: number;
  actual_rates: Record<string, number>;
  pity_trigger_rate: number;
}

export default function Simulator({ session }: Props) {
  const base = `/api/v1/games/${session.gameId}/modules/simulator`;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Economy sim
  const [playerCount, setPlayerCount] = useState('100');
  const [days, setDays] = useState('7');
  const [econSeed, setEconSeed] = useState('42');
  const [econResult, setEconResult] = useState<EconomyResult | null>(null);

  // Progression sim
  const [profileName, setProfileName] = useState('casual');
  const [maxLevel, setMaxLevel] = useState('50');
  const [xpPerLevel, setXpPerLevel] = useState('100');
  const [progResult, setProgResult] = useState<ProgressionResult | null>(null);

  // Loot sim
  const [dropRatesJson, setDropRatesJson] = useState('{"common_sword": 0.5, "rare_gem": 0.05, "legendary_shield": 0.01}');
  const [rolls, setRolls] = useState('1000');
  const [lootSeed, setLootSeed] = useState('42');
  const [lootResult, setLootResult] = useState<LootResult | null>(null);

  const runEconomy = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/economy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_count: Number(playerCount),
          days: Number(days),
          seed: Number(econSeed),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setEconResult(await res.json());
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const runProgression = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/progression`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_name: profileName,
          max_level: Number(maxLevel),
          xp_per_level: Number(xpPerLevel),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setProgResult(await res.json());
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const runLoot = async () => {
    setBusy(true); setError(null);
    try {
      let parsed: Record<string, number>;
      try { parsed = JSON.parse(dropRatesJson); }
      catch { throw new Error('Invalid JSON for drop rates'); }
      const res = await fetch(`${base}/loot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          drop_rates: parsed,
          rolls: Number(rolls),
          seed: Number(lootSeed),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setLootResult(await res.json());
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="Simulator"
        subtitle="Monte Carlo simulations for economy, progression, and loot balance."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Economy Simulation */}
      <div className="card p-4 mb-5">
        <h3 className="text-sm font-semibold mb-3">Economy Simulation</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Player Count</label>
            <input className="input w-28" type="number" value={playerCount} onChange={e => setPlayerCount(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Days</label>
            <input className="input w-20" type="number" value={days} onChange={e => setDays(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Seed</label>
            <input className="input w-24" type="number" value={econSeed} onChange={e => setEconSeed(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={runEconomy}>Run</button>
        </div>
        {econResult && (
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-2">{econResult.daily_snapshots_count} daily snapshots simulated</div>
            {Object.entries(econResult.faucet_sink_ratios).length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Resource</th><th>Faucet/Sink Ratio</th></tr></thead>
                  <tbody>
                    {Object.entries(econResult.faucet_sink_ratios).map(([res, ratio]) => (
                      <tr key={res}>
                        <td className="font-mono text-xs">{res}</td>
                        <td className={ratio > 2 ? 'text-red-400' : 'text-green-400'}>{ratio.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {econResult.inflation_alerts.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-red-400 mb-1">Inflation Alerts</div>
                {econResult.inflation_alerts.map((a, i) => (
                  <div key={i} className="text-xs text-red-300">{a}</div>
                ))}
              </div>
            )}
            {econResult.warnings.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-yellow-400 mb-1">Warnings</div>
                {econResult.warnings.map((w, i) => (
                  <div key={i} className="text-xs text-yellow-300">{w}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progression Simulation */}
      <div className="card p-4 mb-5">
        <h3 className="text-sm font-semibold mb-3">Progression Simulation</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Profile Name</label>
            <input className="input" value={profileName} onChange={e => setProfileName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Max Level</label>
            <input className="input w-20" type="number" value={maxLevel} onChange={e => setMaxLevel(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">XP per Level</label>
            <input className="input w-24" type="number" value={xpPerLevel} onChange={e => setXpPerLevel(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={runProgression}>Run</button>
        </div>
        {progResult && (
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-1">Profile: <span className="font-mono">{progResult.profile}</span></div>
            <div className="text-xs text-fg-muted mb-1">Levels simulated: {progResult.levels}</div>
            <div className="text-sm font-semibold mb-2">Days to max: {progResult.days_to_max.toFixed(1)}</div>
            {progResult.bottlenecks.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-yellow-400 mb-1">Bottlenecks</div>
                {progResult.bottlenecks.map((b, i) => (
                  <div key={i} className="text-xs text-yellow-300">{b}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loot Simulation */}
      <div className="card p-4 mb-5">
        <h3 className="text-sm font-semibold mb-3">Loot Simulation</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Drop Rates (JSON)</label>
            <textarea
              className="input w-full font-mono text-xs"
              rows={3}
              value={dropRatesJson}
              onChange={e => setDropRatesJson(e.target.value)}
            />
          </div>
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="text-xs text-fg-muted block mb-1">Rolls</label>
              <input className="input w-24" type="number" value={rolls} onChange={e => setRolls(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-fg-muted block mb-1">Seed</label>
              <input className="input w-24" type="number" value={lootSeed} onChange={e => setLootSeed(e.target.value)} />
            </div>
            <button className="btn btn-primary" disabled={busy} onClick={runLoot}>Run</button>
          </div>
        </div>
        {lootResult && (
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-2">Total rolls: {lootResult.total_rolls}</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Item</th><th>Actual Rate</th></tr></thead>
                <tbody>
                  {Object.entries(lootResult.actual_rates).map(([item, rate]) => (
                    <tr key={item}>
                      <td className="font-mono text-xs">{item}</td>
                      <td>{(rate * 100).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-fg-muted mt-1">Pity trigger rate: {(lootResult.pity_trigger_rate * 100).toFixed(2)}%</div>
          </div>
        )}
      </div>
    </Container>
  );
}
